import { Combobox, ComboboxExtrasButton } from '@squareup/dex-ui';
import { Markdown } from '@squareup/dex-ui-markdown';
import { useApiExplorerCustomAttributeSchema } from '@squareup/dex-ui-shared-api-explorer-providers';
import { Box } from '@squareup/dex-ui-shared-base';
import { toIgnitionDataType } from '@squareup/dex-utils-oas';
import {
  AnyFieldOptions,
  constructSchemaWithRef,
  CUSTOM_SCHEMA_KEY,
  getSchemaValueByKey,
  parseSchemaValueFromCurrent,
  SchemaCustomDefinition,
  SchemaType,
} from '@squareup/dex-utils-shared-api-explorer';
import React, { FC, useCallback, useMemo, useState } from 'react';

import { ApiExplorerFieldSpecialValuesDropdown } from './helpers/ApiExplorerFieldSpecialValuesDropdown';
import { useSchemaProps } from './hooks/nullable-hooks';
import { ApiExplorerJsonField } from './primitives/ApiExplorerJsonField';
import { ApiExplorerFieldProps } from './props';
import { useApiExplorerFieldContext } from './providers/ApiExplorerFieldProvider';

interface Props extends ApiExplorerFieldProps {}

const parseSchemaTypes = (schema: SchemaType) => {
  let types;
  if (Object.keys(schema).length > 0) {
    // Appending custom schema to the end of the json because it is not part of the schema types json
    const definitions: SchemaType['definitions'] = {
      ...schema.definitions,
      ...SchemaCustomDefinition,
    };
    types = Object.keys(definitions).map((key: string) => ({
      value: getSchemaValueByKey(key),
      description: (
        <Markdown
          markdown={(definitions[key] as { description: string }).description}
          textVariant="20"
          textColor="currentColor"
        />
      ),
    }));
  }
  return types;
};

// Custom attribute schema field
const ApiExplorerSchemaField: FC<Props> = ({
  currentValue,
  onChange,
  path,
}) => {
  const { isNullable } = useApiExplorerFieldContext();

  const schema = useApiExplorerCustomAttributeSchema();

  const [comboboxValue, setComboboxValue] = useState<string | undefined>(
    parseSchemaValueFromCurrent(currentValue)
  );

  const schemaValues = useMemo(
    () => (schema ? parseSchemaTypes(schema) : []),
    [schema]
  );

  const onSchemaTypeChange = useCallback(
    (value: string) => {
      setComboboxValue(value);
      if (value === CUSTOM_SCHEMA_KEY) {
        onChange(undefined, path);
      } else {
        onChange(constructSchemaWithRef(value), path);
      }
    },
    [onChange, path]
  );

  const {
    placeholder,
    icon,
    comboboxValue: newComboboxValue,
  } = useSchemaProps('schema', currentValue, comboboxValue);

  return (
    <>
      <Combobox
        comboboxValues={schemaValues}
        name="schema"
        placeholder={placeholder}
        leadingAccessory={icon}
        onChange={onSchemaTypeChange}
        readOnly={true}
        value={newComboboxValue}
        extrasButton={
          <ApiExplorerFieldSpecialValuesDropdown
            currentValue={currentValue}
            showNullable={isNullable}
            showEmptyString={false}
            path={path}
            onChange={onChange}
            trigger={<ComboboxExtrasButton />}
          />
        }
      />
      {newComboboxValue === CUSTOM_SCHEMA_KEY && (
        <Box margin={{ top: '1x' }}>
          <ApiExplorerJsonField
            currentValue={JSON.stringify(currentValue)}
            onChange={onChange}
            path={path}
            name={'schema-json'}
            type={toIgnitionDataType(
              AnyFieldOptions.OBJ.toLowerCase() as 'object'
            )}
          />
        </Box>
      )}
    </>
  );
};

export { ApiExplorerSchemaField };
